import {
  blue,
  FaroCheckbox,
  FaroText,
  grey,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Skeleton, Stack } from "@mui/material";
import { PDFPageProxy } from "pdfjs-dist/types/src/display/api";
import { memo, useEffect, useState, type JSX } from "react";

type PdfPageInfo = {
  /** The PDF page for which the thumbnail should be generated */
  pageData: PDFPageProxy;
  /** The number of the page in the PDF document with range starting from 1 to total number of pages in the PDF */
  pageNumber: number;
};

export type PdfPageThumbnailProps = {
  /** The  info on the PDF page for which the thumbnail should be generated */
  pageInfo?: PdfPageInfo;
  /** The size of the thumbnail in pixels @default 512 */
  thumbnailSize?: number;
  /**
   * callback used to update the page previewed from multi-pages PDF file
   *
   * @param pageNumber selected page number to preview (numerated from 1 to numberOfPdfPages)
   */
  onUpdatePreviewPage?(pageNumber: number): void;
  /** Whether this thumbnail is currently selected */
  isSelected: boolean;
  /** Whether this thumbnail is currently being previewed */
  isPreviewed: boolean;
  /**
   * callback used to add a page to the selection from multi-pages PDF file
   *
   * @param pageNumber selected page number (numerated from 1 to numberOfPdfPages)
   */
  onAddToSelection?(pageNumber: number): void;
};

/** @returns A single PDF page thumbnail */
export const PdfPageThumbnail = memo(function PdfPageThumbnail({
  pageInfo,
  thumbnailSize = 512,
  onUpdatePreviewPage,
  isSelected,
  onAddToSelection,
  isPreviewed,
}: PdfPageThumbnailProps): JSX.Element {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();
  useEffect(() => {
    // Here, we generate the thumbnail directly from the PDF renderer with the wanted size
    async function renderThumbnail(page: PDFPageProxy): Promise<void> {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      assert(context, "Canvas context should not be null");
      const viewport = page.getViewport({ scale: 1 });
      const aspectRatio = viewport.height / viewport.width;
      const scale = thumbnailSize / viewport.width;
      const finalViewport = page.getViewport({ scale });
      canvas.width = thumbnailSize;
      canvas.height = thumbnailSize * aspectRatio;

      await page.render({ canvasContext: context, viewport: finalViewport })
        .promise;
      setThumbnailUrl(canvas.toDataURL());
    }

    // Try to render the thumbnail if the pdfPage is available
    if (pageInfo?.pageData) {
      renderThumbnail(pageInfo.pageData);
    }
  }, [pageInfo?.pageData, thumbnailSize]);

  if (!thumbnailUrl || !pageInfo) {
    return (
      <Skeleton
        sx={{
          variant: "rectangular",
          width: "100%",
          height: "15vh",
          bgcolor: `${grey[500]}26`,
        }}
      />
    );
  }

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", position: "relative" }}
      aria-label={`Page ${pageInfo.pageNumber}`}
    >
      <Stack
        sx={{
          // The checkbox parent label is using large positive and negative margin values that we want to override here.
          marginLeft: "16px",
          marginRight: "-16px",
        }}
      >
        <FaroCheckbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onAddToSelection?.(pageInfo.pageNumber);
          }}
        />
      </Stack>
      <Stack
        sx={{
          position: "relative",
          border: isPreviewed ? `2px solid ${blue[500]}` : "none",
          cursor: "pointer",
        }}
      >
        <img
          src={thumbnailUrl}
          alt={`Page ${pageInfo.pageNumber}`}
          style={{
            display: "block",
            width: "auto",
            height: "auto",
            maxWidth: "100%",
          }}
          onClick={() => onUpdatePreviewPage?.(pageInfo.pageNumber)}
        />
        <Stack
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            willChange: "transform",
            backgroundColor: `${grey[0]}`,
            padding: "4px 12px",
            borderRadius: "50px",
            border: `1px solid ${grey[300]}`,
            pointerEvents: "none",
            minWidth: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <FaroText variant="bodyM">
            Page{" "}
            <TranslateVar name="pageNumber">{pageInfo.pageNumber}</TranslateVar>
          </FaroText>
        </Stack>
      </Stack>
    </Stack>
  );
});
